import type { BrandConfig } from './types';
import { BRAND_HTML_META } from './htmlMeta';
import { hypeappBrand } from './hypeapp';
import { shohinJohinBrand } from './shohin-johin';
import logo from '../../assets/usodeki_logo_transparent.png';

/**
 * 嘘でできた世界 (usodeki) ブランド設定
 *
 * Light Media が自社運用するテンプレート特化ブランド。
 * バックエンドのキーは Light Media のものを共用する「Light Media キー利用型」パートナー。
 * 詳細: docs/partner-setup-lightmedia-keys.md
 *
 * take8 と同様 hypeapp をベースにしつつ、以下が特徴:
 * - ホームはテンプレート一覧のみを表示する専用バリアント ('usodeki')
 * - MainNav のタブ (スタジオ / 商品データ / AI俳優) を出さない (mainNavTabs: false)。
 *   ルート自体は残るため、テンプレートカード → /studio/image への遷移は可能
 * - 動画 (シナリオ) 導線はホームに置かない (静止画テンプレートのみ)
 */
export const usodekiBrand: BrandConfig = {
  ...hypeappBrand,
  id: 'usodeki',
  serviceName: BRAND_HTML_META.usodeki.serviceName,
  companyName: 'Light Media, Inc.',
  colorScheme: 'light', // ロゴが白背景 + 黒文字のため明色基調
  logoWhite: logo,
  logoDark: logo, // 白背景ロゴなので明色テーマもそのまま使う
  favicon: BRAND_HTML_META.usodeki.favicon,
  colors: {
    // ロゴのホログラム・グリッチ (ラベンダー→シアン→ピンク) から採色。
    // 中高生向けの“ゆめかわ/ホログラム”感を白ベースで表現するため、主役は電気的な
    // ペリウィンクル紫に統一し、CTA だけロゴのグリッチを写したグラデ (usodeki-theme.css の
    // --generate-button-bg) にする。
    //   primary = ソフトなペリウィンクル紫 (大面積の主要面。半透明ウォッシュで使われる)
    //   sub     = パステルシアン (補助面)
    //   accent  = 電気的なペリウィンクル紫 (主要 CTA / 選択 / フォーカス / 強調)
    primary: '#B0A3E0',
    primaryHover: '#9C8DD6',
    primaryRgb: '176 163 224',
    subSolid: '#79D5E8',
    subSolidHover: '#5FC5DC',
    subRgb: '121 213 232',
    // 白ベース UI で CTA・選択状態が沈まないよう、パステルより一段濃い電気紫
    accentSolid: '#7C5CE6',
    accentSolidHover: '#6A48D6',
    accentRgb: '124 92 230',
    // エディトリアル基調の生成り紙。ホーム背景は usodeki-theme.css の html でも
    // 同色を敷く (safe-area まで紙色に統一)。
    // フッター下に色差 (グラデーション) が出ないよう、開始/終了とも同じ紙色にして
    // キャンバス (.app-shell::before) をフラットな生成り紙一色にする。
    bgGradientStart: '#F6F3EE',
    bgGradientEnd: '#F6F3EE',
  },
  features: {
    ...hypeappBrand.features,
    fishLicensedVoicesDefault: false, // 権利処理済みの既定オンは SAITE のみ (hypeapp 継承の打ち消し)
    gtm: BRAND_HTML_META.usodeki.gtm,
    navigationGuide: false, // ナビガイドはスタジオ導線前提のため出さない
    omakasePlan: false,
    signupEnabled: true,
    mainNavTabs: false, // スタジオ / 商品データ / AI俳優 タブを非表示
    appChrome: false, // ヘッダー/フッター無し (ホーム + 素材パネルのみのモバイル特化構成)
    publicHome: true, // ホーム (`/`) は未ログインでも閲覧可。生成ボタンでログインへ誘導
    autoPersonalWorkspace: true, // 組織概念を隠し、オンボーディングは自動で個人WS作成
    materialsNavTab: true, // タブ類は出さないが「素材」だけは表示
    overlayPageNavigation: true, // 利用状況/マイページはホーム上のオーバーレイで開く (1画面構成)
    minimalUserMenu: true, // メニューは 利用状況/マイページ/お問い合わせ/ログアウト のみ
    simpleMaterialsUi: true, // 素材は個人フォルダのみ・取得元フィルタのみ・カードは画像のみ
    dashboardPointsOnly: true, // 中高生向けに使用量/ストレージ統計を出さずポイント残高のみ
    simpleProfilePage: true, // マイページから 所属組織/権限/全身写真(カメオ) を非表示
    creatorProgram: true, // ACP: テンプレにクリエイター名/ランクバッジを表示
  },
  // 用語は default (商品上品) と同じ
  labels: {
    ...shohinJohinBrand.labels,
  },
  urls: {
    website: 'https://www.usodeki.com',
    contact: 'https://www.lightmedia.jp/contact',
    privacy: 'https://www.lightmedia.jp/privacy',
  },
  // usodeki はログインからの自動作成を許可する partner (login_auto_provision_enabled=true)
  // のため ACCOUNT_NOT_FOUND は実運用では発火しないが、型上必須のため
  // default(商品上品) と同値を設定しておく (hypeappBrand ベースのため明示が必要)。
  auth: {
    notFoundGuidance: shohinJohinBrand.auth.notFoundGuidance,
  },
  // 初回アンケートは職業 1 問のみ (backend SURVEY_CONFIGS の usodeki と一致)
  surveyQuestions: ['occupation'],
  // メニュー末尾に姉妹サービスへの外部リンクを出す
  menuExternalLinks: [
    { label: '商品上品', url: 'https://show.lightmedia.jp' },
    { label: 'TAKE 8', url: 'https://take8.jp' },
  ],
  homePageVariant: 'usodeki',
};
